import { api } from "./api";

/** What this browser can do about push, in the order a person needs to hear it. */
export type PushSupport =
  | "unsupported"
  /** iPhone or iPad: web push only works once the site is added to the Home Screen. */
  | "ios-needs-install"
  | "denied"
  | "ready";

function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushSupport(): PushSupport {
  const hasApis = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (!hasApis) return isIos() && !isStandalone() ? "ios-needs-install" : "unsupported";
  if (!window.isSecureContext) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  return "ready";
}

function keyToBytes(base64url: string): Uint8Array {
  const padded = base64url + "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

function deviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const os = /Android/.test(ua)
    ? "Android"
    : isIos()
      ? "iOS"
      : /Windows/.test(ua)
        ? "Windows"
        : /Mac OS X/.test(ua)
          ? "macOS"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return os ? `${browser} on ${os}` : browser;
}

/**
 * Ask permission (must be called from a click), subscribe this browser, and
 * tell the server. Throws a message fit to show a person.
 */
export async function enablePush(): Promise<void> {
  const key = await api<{ enabled: boolean; publicKey: string | null }>("/push/key");
  if (!key.enabled || !key.publicKey)
    throw new Error("Push notifications are not switched on for this server.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      "Notifications were blocked. Allow them for this site in your browser's settings, then try again.",
    );
  }
  const reg = await navigator.serviceWorker.ready;
  const subscription =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyToBytes(key.publicKey) as BufferSource,
    }));
  await api("/push/subscribe", {
    method: "POST",
    body: { ...subscription.toJSON(), deviceLabel: deviceLabel() },
  });
}

export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await api("/push/subscribe", { method: "DELETE", body: { endpoint } });
}
